<template>
  <div>
    <v-layout row wrap>
      <v-flex xs12 sm6>
        <v-textarea
          v-model="form.description"
          hint="Detailed description of the time spent. Bad logs will be deleted and unpaid"
          label="Description"
          :error-messages="mergedErrors"
          auto-grow
          counter
          :error-count="2"
          rows="1"
          @keyup.ctrl.enter.prevent="saveTime"
          outline
        ></v-textarea>
      </v-flex>
      <v-flex xs12 sm2>
        <v-text-field
          v-model="form.duration"
          label="Time"
          hint="Format - 2h 15m"
          :error-messages="errorMessages('duration')"
          @keyup.ctrl.enter.prevent="saveTime"
          outline
        ></v-text-field>
      </v-flex>
      <v-flex xs12 sm2>
        <v-select
          v-model="form.type"
          :items="timeTypes"
          label="Type"
          :error-messages="errorMessages('type')"
          outline
          item-value="value"
          item-text="title"
        ></v-select>
      </v-flex>
      <v-flex xs12 sm2>
        <v-btn
          block
          color="info"
          @click="saveTime"
          :disabled="!isTrackTimeButtonActive || loadIndicator"
        >
          Track time
        </v-btn>
        <div v-if="isDesktop" class="times-send_hint">Ctrl + Enter to send</div>
      </v-flex>
    </v-layout>
    <log-time-modal
      ref="timeModal"
      :parentDuration="form.duration"
      :parentDescription="form.description"
      :timesId="newTimesEntity.id"
      @childDuration="changeDuration"
      @childDescription="changeDescription"
      @deleteForm="deleteForm"
    ></log-time-modal>
    <v-divider></v-divider>
    <v-data-table
      :headers="headers"
      :pagination.sync="pagination"
      :total-items="pagination.totalItems"
      :items="items"
    >
      <template slot="items" slot-scope="props">
        <tr>
          <td class="text-xs-left">{{ props.item.user | getText('name', 'without name') }}</td>
          <td class="text-xs-left">{{ props.item.created_at }}</td>
          <td class="text-xs-left">{{ props.item.duration }}</td>
          <td class="text-xs-left">{{ props.item.description }}</td>
          <td class="text-xs-left">{{ getTimeTypeText(props.item.type) }}</td>
          <td class="text-xs-left" v-if="featureAllowed($features.TIMES_APPROVE_BUTTON)">
            <v-btn
              v-if="props.item.approved"
              class="ma-0"
              color="error"
              small
              @click="changeAppruve(props.item)"
            >
              Cancel
            </v-btn>
            <v-btn
              v-else
              class="ma-0"
              color="success"
              small
              @click="changeAppruve(props.item)"
            >
              Approved
            </v-btn>
          </td>
          <td v-else>
            {{ props.item.approved ? "Yes" : "No" }}
          </td>
          <td class="text-xs-right">
            <div class="table_actions_btns">
              <v-btn
                v-if="isEditAllowed(props.item)"
                icon class="mr-1"
                color="success"
                :ripple="false"
                small
                @click="editTime(props.item)"
              >
                <v-icon size="16">edit</v-icon>
              </v-btn>
              <remove-dialog :yes="() => remove(props.item)">
                <v-btn v-if="isDeleteAllowed(props.item)" color="error" icon small class="mr-0 ml-1" :ripple="false">
                  <v-icon size="16">delete</v-icon>
                </v-btn>
              </remove-dialog>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import * as mixins from '@/mixins/'
import logTimeModal from "@/components/tasks/logTimeModal";
import timeTypes from "@/entities/timeTypes";

export default {
  name: 'TaskTimes',
  props: {
    task_id: {
      type: Number,
      default: ''
    },
    notification: {
      type: Object,
      default: function () {
        return {}
      }
    }
  },
  mixins: [mixins.removable, mixins.table, mixins.user, mixins.errors, mixins.preloader],
  data() {
    return {
      api: this.$api.times,
      newTimesEntity: {},
      timeTypes: timeTypes,
      headers: [
        {text: 'User', align: 'left', sortable: true, value: 'firstname'},
        {text: 'Date/time', align: 'left', sortable: true, value: 'created_at'},
        {text: 'Hours', align: 'left', sortable: false},
        {text: 'Description', align: 'left', sortable: false, width: '50%'},
        {text: 'Type', align: 'left', sortable: false, value: 'type'},
        {text: 'Approved', align: 'left', sortable: true, value: 'approved', hidden: true},
        {text: 'Actions', align: 'right', sortable: false},
      ],
      filter: {
        tasks: [this.task_id]
      },
      form: {
        duration: '',
        description: '',
        type: 'billable'
      },
      loadIndicator: false,
      showLogTimeModal: false,
    }
  },
  computed: {
    mergedErrors() {
      const backendErrors = this.errorMessages("description") || [];
      const validationError = this.maxLengthRule(this.form.description);

      return [...backendErrors, ...(validationError ? [validationError] : [])];
    },
    isTrackTimeButtonActive() {
      return this.form.description.length <= 135
    }
  },
  methods: {
    maxLengthRule(value) {
      return value && value.length > 135 ? "Maximum 135 characters allowed" : "";
    },
    openTimeModal() {
      this.$refs.timeModal.open()
    },
    changeDuration(event) {
      this.form.duration = event
    },
    changeDescription(event) {
      this.form.description = event
    },
    deleteForm() {
      this.form = {duration: '', description: ''}
    },
    saveTime() {
      this.loadIndicator = true
      this.$api.times.save({...this.form, task_id: this.task_id}).then((response) => {
        if(this.notification.state) {
          this.openTimeModal()
        } else {
          this.form = {duration: '', description: ''}
        }
        this.$notify({
          group: 'success',
          type: 'success',
          text: 'Time saved'
        })
        this.newTimesEntity = response.data
        this.loadIndicator = false
        this.errors = false
        this.background(this.load())
      }, errors => {
        this.loadIndicator = false
        this.errors = errors.errors
      })
    },
    editTime(time) {
      this.form = time
    },
    setDuration(duration) {
      this.form.duration = duration
    },
    changeAppruve(time) {
      time.approved == 1 ? time.approved = 0 : time.approved = 1
      this.$api.times.save(time).then(response => {
        this.$notify({
          group: 'success',
          type: 'success',
          text: 'Saved'
        })
        this.background(this.load())
      }, err => {
         console.log('components/tasks/times.vue changeAppruve', err)
      })
    },
    isEditAllowed(item) {
      return !item.approved && this.featureAllowed(this.$features.TIMES_EDIT_BUTTON) && this.user.id == item.user_id || this.user.instanceUsers[0].roles[0].id == 1
    },
    isDeleteAllowed(item) {
      return this.featureAllowed(this.$features.TIMES_DELETE_BUTTON) && this.user.id == item.user_id || this.user.instanceUsers[0].roles[0].id == 1
    },
    getTimeTypeText(type) {
      if (type) {
        const existedType = this.timeTypes.find(item => item.value === type)
        return existedType ? existedType.title : 'no type'
      }

      return 'no type'
    }
  },
  components: {
    'log-time-modal': logTimeModal,
  }
}
</script>
